import { useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormField } from "@/components/FormBits";
import { PriceTableEditor, emptyTable, editableTable, tablePayload } from "@/components/PriceTableEditor";
import { api, imgUrl, formatError } from "@/lib/api";

const EMPTY = { name: "", description: "", category: "", pricing_type: "m2", price: "", reseller_price: "", reseller_discount: false, unit_label: "m²", image_url: "", active: true };

export function ProductFormDialog({ product, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...product, reseller_price: product.reseller_price ?? "" });
  const [busy, setBusy] = useState(false);
  const [table, setTable] = useState(() => editableTable(product.price_table));
  const isTable = f.pricing_type === "unit" && !!table;
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setF((prev) => ({ ...prev, image_url: data.url }));
      toast.success("Imagem enviada");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const tp = isTable ? tablePayload(table) : null;
    const tablePrices = tp ? tp.prices.flat().filter((v) => v > 0) : [];
    if (isTable && !tablePrices.length) return toast.error("Preencha pelo menos um preço na tabela");
    setBusy(true);
    const payload = { name: f.name, description: f.description, category: f.category || "Geral", pricing_type: f.pricing_type,
      price: isTable ? Math.min(...tablePrices) : Number(f.price), reseller_price: !isTable && f.reseller_price ? Number(f.reseller_price) : null, reseller_discount: !!f.reseller_discount,
      unit_label: f.pricing_type === "m2" ? "m²" : f.unit_label || "unidade", image_url: f.image_url, active: f.active, price_table: tp };
    try {
      if (product.id) await api.put(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Produto salvo");
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="product-form-dialog">
        <DialogHeader><DialogTitle>{product.id ? "Editar produto" : "Novo produto"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <FormField id="product-name" label="Nome" required value={f.name} onChange={set("name")} className="sm:col-span-2" />
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs font-semibold text-slate-600">Descrição</Label>
            <Textarea rows={4} value={f.description} onChange={set("description")} data-testid="input-product-description" />
          </div>
          <FormField id="product-category" label="Categoria" value={f.category} onChange={set("category")} placeholder="Ex.: Banners e Lonas" />
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">Tipo de venda</Label>
            <Select value={f.pricing_type} onValueChange={(v) => setF({ ...f, pricing_type: v, unit_label: v === "m2" ? "m²" : "unidade" })}>
              <SelectTrigger className="h-11" data-testid="select-pricing-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="m2" data-testid="pricing-type-m2">Por metro quadrado (m²)</SelectItem>
                <SelectItem value="unit" data-testid="pricing-type-unit">Por unidade</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!isTable && <FormField id="product-price" label={f.pricing_type === "m2" ? "Preço por m² (R$)" : "Preço por unidade (R$)"} type="number" step="0.01" min="0.01" required value={f.price} onChange={set("price")} />}
          {!isTable && <FormField id="product-reseller-price" label={`Preço revendedor ${f.pricing_type === "m2" ? "por m²" : "por unidade"} (R$, opcional)`} type="number" step="0.01" min="0.01" placeholder="Vazio = regra dos 50% da coluna Revendedor" value={f.reseller_price} onChange={set("reseller_price")} />}
          {f.pricing_type === "unit" && <FormField id="product-unit-label" label="Unidade de venda" placeholder="unidade, milheiro, pacote..." value={f.unit_label} onChange={set("unit_label")} />}
          {f.pricing_type === "unit" && (
            <div className="flex items-start gap-3 sm:col-span-2">
              <Switch checked={!!table} onCheckedChange={(v) => setTable(v ? editableTable(product.price_table) || emptyTable() : null)} data-testid="switch-price-table" />
              <div>
                <p className="text-sm font-semibold">Tabela de preços por opção e quantidade</p>
                <p className="text-xs text-slate-500">Ex.: cartão de visita 4x0, 4x1 ou 4x4 em 500, 1000 ou 2000 unidades, cada combinação com seu valor. Revendedores recebem 50% quando a caixinha da coluna Revendedor estiver marcada, ou o preço de revenda que você digitar em cada pacote.</p>
              </div>
            </div>
          )}
          {isTable && <PriceTableEditor table={table} onChange={setTable} />}
          <div className="space-y-2 sm:col-span-2">
            <Label className="text-xs font-semibold text-slate-600">Imagem</Label>
            <div className="flex items-center gap-4">
              {f.image_url ? <img src={imgUrl(f.image_url)} alt="" className="h-20 w-20 rounded object-cover" data-testid="product-image-preview" /> : <div className="h-20 w-20 rounded bg-slate-100" />}
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold hover:border-sky-500">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Enviar imagem
                <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="input-product-image-file" />
              </label>
            </div>
            <FormField id="product-image-url" label="ou cole a URL da imagem" value={f.image_url} onChange={set("image_url")} />
          </div>
          <div className="flex items-center gap-3 sm:col-span-2">
            <Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="switch-product-active" />
            <span className="text-sm">Produto ativo na loja</span>
          </div>
          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="btn-cancel-product">Cancelar</Button>
            <Button type="submit" disabled={busy || uploading} className="bg-[#0099DD] hover:bg-sky-700" data-testid="btn-save-product">{busy ? "Salvando..." : "Salvar"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
